import React from 'react';
import { motion } from 'framer-motion';
import { renderPartnerLogo } from './utils';
import Button from '../../../../components/ui/Button';
import Icon from '../../../../components/AppIcon';

const OrganizersSection = () => {
  // Social media links
  const socialLinks = [
    {
      name: 'Facebook',
      icon: 'Facebook',
      url: 'https://www.facebook.com/people/General-Union-of-Syrian-Students/61556795854406/?rdid=nEpNLZ2NtdfbaSYg&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1AAVKtJke2%2F'
    },
    {
      name: 'Instagram',
      icon: 'Instagram',
      url: 'https://www.instagram.com/guofsyrians'
    },
    {
      name: 'X',
      icon: 'X',
      url: 'https://x.com/guofsyrians'
    },
    {
      name: 'YouTube',
      icon: 'Youtube',
      url: 'https://www.youtube.com/@guofsyrian'
    }
  ];

  return (
    <section id="who-are-we" className="relative py-16 lg:py-24 px-4 bg-gradient-to-b from-[#114845]/5 via-[#114845]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center text-center space-y-8"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center mb-6 h-32"
          >
            {renderPartnerLogo({
              name: "الإتحاد العام لطلبة سوريا",
              logo: "/assets/logos/guofss_logo.png",
              logoAlt: "GUOFSS logo",
              website: "http://guofsyrians.org"
            })}
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
            dir="rtl"
          >
            الاتحاد العام لطلبة سوريا المظلة الجامعة والصوت الرائد
          </motion.h2>

          {/* Body Text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base md:text-lg text-gray-700 leading-relaxed max-w-3xl"
            dir="rtl"
          >
            يُعد الاتحاد المؤسسة النقابية التي تمثل الطلبة السوريين في الجامعات التركية، وقد انطلق من رؤية واضحة تؤمن بأن طلاب اليوم هم قادة الغد، ليكون السند الحقيقي للطالب في مسيرته التعليمية والاجتماعية. يعمل الاتحاد عبر هيكلية مؤسسية واسعة تضم 44 فرعاً لتمثيل الصوت الطلابي، ويسعى لخلق بيئة تعليمية تنصف الطالب وتبني الإنسان.
          </motion.p>

          {/* Primary CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="pt-4"
          >
            <Button
              asChild
              variant="default"
              size="lg"
            >
              <a
                href="http://guofsyrians.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                تصفح الموقع الرسمي للاتحاد
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </Button>
          </motion.div>

          {/* Social Media Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col items-center gap-4 pt-4"
          >
            <p className="text-sm md:text-base text-gray-600" dir="rtl">
              أو تابع آخر أخبارنا عبر منصاتنا الرسمية:
            </p>
            <div className="flex gap-4 justify-center">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 bg-gray-100 hover:bg-primary hover:text-white"
                  aria-label={social.name}
                >
                  <Icon
                    name={social.icon}
                    size={20}
                    color="currentColor"
                  />
                </a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default OrganizersSection;

